<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useTrack } from 'dashboard/composables';
import { FUNNEL_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import NervenetFrame from './NervenetFrame.vue';

const { t } = useI18n();
const router = useRouter();
const { accountId, accountScopedRoute } = useAccount();
const inboxes = useMapGetter('inboxes/getInboxes');

const agentConfiguredKey = `nervenet_agent_configured:${accountId.value}`;
const aiConfigured = ref(localStorage.getItem(agentConfiguredKey) === 'true');
const dismissed = ref(
  sessionStorage.getItem(SESSION_STORAGE_KEYS.NERVENET_NEXT_STEP_DISMISSED) ===
    'true'
);

const showCard = computed(
  () => aiConfigured.value && inboxes.value.length === 0 && !dismissed.value
);

const iframeUrl = computed(
  () =>
    `${window.chatwootConfig.nervenetBaseUrl}/accounts/${accountId.value}/agent`
);

const handleMessage = event => {
  if (event.data?.type === 'nervenet:agent_status') {
    const configured = !!event.data.configured;
    localStorage.setItem(agentConfiguredKey, configured ? 'true' : 'false');
    aiConfigured.value = configured;
  }
  if (event.data?.type === 'nervenet:playground_message_sent') {
    useTrack(FUNNEL_EVENTS.PLAYGROUND_MESSAGE_SENT);
  }
};

const onConnect = () => {
  router.push(accountScopedRoute('settings_inbox_new'));
};

const onSkip = () => {
  sessionStorage.setItem(
    SESSION_STORAGE_KEYS.NERVENET_NEXT_STEP_DISMISSED,
    'true'
  );
  dismissed.value = true;
};

onMounted(() => window.addEventListener('message', handleMessage));
onUnmounted(() => window.removeEventListener('message', handleMessage));
</script>

<template>
  <NervenetFrame :src="iframeUrl">
    <transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-4"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-4"
    >
      <aside
        v-if="showCard"
        class="absolute bottom-6 ltr:left-6 rtl:right-6 z-40 w-80 max-w-[calc(100vw-3rem)] rounded-xl bg-n-solid-2 border border-n-weak shadow-xl backdrop-blur-md p-4 flex flex-col gap-3"
      >
        <header class="flex items-start gap-3">
          <span
            class="flex-shrink-0 size-9 rounded-full bg-n-teal-3 grid place-content-center"
          >
            <span class="i-lucide-check size-4 text-n-teal-11" />
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-n-slate-12 m-0">
              {{ t('HOME.SETUP.BANNER_HEADING') }}
            </p>
            <p class="text-xs text-n-slate-10 mt-1 leading-relaxed mb-0">
              {{ t('HOME.SETUP.BANNER_BODY') }}
            </p>
          </div>
          <button
            type="button"
            class="flex-shrink-0 size-6 rounded-md grid place-content-center text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors duration-150"
            :aria-label="t('GENERAL_SETTINGS.DISMISS')"
            @click="onSkip"
          >
            <span class="i-lucide-x size-3.5" />
          </button>
        </header>
        <button
          type="button"
          class="inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-n-brand text-white hover:bg-n-brand/90 transition-colors duration-150"
          @click="onConnect"
        >
          {{ t('HOME.SETUP.BANNER_CTA') }}
          <span class="i-lucide-arrow-right size-3.5" />
        </button>
      </aside>
    </transition>
  </NervenetFrame>
</template>
